import { NavigateFunction } from "react-router";
import { errorMonitor } from "./errorMonitoring";
import { isLocalDevelopment } from "./environment";

export type ErrorType =
  | "NETWORK_ERROR"
  | "TIMEOUT_ERROR"
  | "PERMISSION_ERROR"
  | "NOT_FOUND_ERROR"
  | "SERVER_ERROR"
  | "MAINTENANCE_ERROR"
  | "UNKNOWN_ERROR";

export type ErrorRoutingOptions = {
  navigate: NavigateFunction;
  error: Error | any;
  context?: string;
  fallbackRoute?: string;
};

/**
 * Mapeamento de códigos de erro HTTP para tipos de erro
 */
const HTTP_STATUS_TO_ERROR_TYPE: Record<number, ErrorType> = {
  400: "UNKNOWN_ERROR",
  401: "PERMISSION_ERROR",
  403: "PERMISSION_ERROR",
  404: "NOT_FOUND_ERROR",
  408: "TIMEOUT_ERROR",
  429: "SERVER_ERROR",
  500: "SERVER_ERROR",
  502: "SERVER_ERROR",
  503: "MAINTENANCE_ERROR",
  504: "TIMEOUT_ERROR",
};

/**
 * Mapeamento de tipos de erro para rotas das páginas
 */
const ERROR_TYPE_TO_ROUTE: Record<ErrorType, string> = {
  NETWORK_ERROR: "/offline",
  TIMEOUT_ERROR: "/timeout",
  PERMISSION_ERROR: "/forbidden",
  NOT_FOUND_ERROR: "/not-found",
  SERVER_ERROR: "/server-error",
  MAINTENANCE_ERROR: "/maintenance",
  UNKNOWN_ERROR: "/server-error",
};

/**
 * Determina o tipo de erro baseado no erro recebido
 */
export function determineErrorType(error: any): ErrorType {
  // Verificar estado da conectividade primeiro
  const isOffline = !navigator.onLine;

  if (isOffline) {
    // Em desenvolvimento local, não tratar offline como NETWORK_ERROR crítico
    if (isLocalDevelopment()) {
      return "UNKNOWN_ERROR"; // Será tratado como erro não crítico
    }
    return "NETWORK_ERROR";
  }

  // Verificar se é um erro do Axios com response
  if (error?.response?.status) {
    const status = error.response.status;

    // Casos especiais para melhores mensagens
    if (status === 401 && error?.response?.data?.message?.includes("token")) {
      return "PERMISSION_ERROR"; // Token expirado
    }

    if (
      status === 503 &&
      error?.response?.data?.message?.includes("maintenance")
    ) {
      return "MAINTENANCE_ERROR";
    }

    return HTTP_STATUS_TO_ERROR_TYPE[status] || "UNKNOWN_ERROR";
  }

  // Verificar erros de timeout com mais precisão
  if (
    error?.code === "ECONNABORTED" ||
    error?.message?.includes("timeout") ||
    error?.message?.includes("ETIMEDOUT")
  ) {
    return "TIMEOUT_ERROR";
  }

  // Análise aprimorada de erros de rede/conectividade
  const networkErrorCodes = [
    "ERR_NETWORK",
    "ERR_CONNECTION_REFUSED",
    "ERR_CONNECTION_RESET",
    "ERR_CONNECTION_ABORTED",
    "ECONNREFUSED",
    "ECONNRESET",
    "ENETUNREACH",
    "EHOSTUNREACH",
  ];

  const hasNetworkErrorCode = networkErrorCodes.some(
    (code) => error?.code === code || error?.message?.includes(code)
  );

  if (hasNetworkErrorCode || error?.message?.includes("Network Error")) {
    // Tentar determinar se é problema de rede ou servidor
    if (navigator.onLine) {
      // Se tem internet mas falha conexão, provavelmente é servidor
      if (
        error?.code === "ECONNREFUSED" ||
        error?.message?.includes("ECONNREFUSED")
      ) {
        return "SERVER_ERROR"; // Servidor não está rodando
      }

      // Para outros erros de rede com internet, assumir problema de servidor
      return "SERVER_ERROR";
    } else {
      // Sem internet = erro de rede
      if (isLocalDevelopment()) {
        return "UNKNOWN_ERROR";
      }
      return "NETWORK_ERROR";
    }
  }

  // Verificar erros de permissão com mais contexto
  const permissionKeywords = [
    "permission",
    "unauthorized",
    "forbidden",
    "access denied",
    "token expired",
    "invalid token",
  ];

  const hasPermissionError = permissionKeywords.some(
    (keyword) =>
      error?.message?.toLowerCase().includes(keyword) ||
      error?.response?.data?.message?.toLowerCase().includes(keyword)
  );

  if (hasPermissionError) {
    return "PERMISSION_ERROR";
  }

  // Verificar erros relacionados à manutenção
  const maintenanceKeywords = [
    "maintenance",
    "under maintenance",
    "temporarily unavailable",
  ];
  const hasMaintenanceError = maintenanceKeywords.some(
    (keyword) =>
      error?.message?.toLowerCase().includes(keyword) ||
      error?.response?.data?.message?.toLowerCase().includes(keyword)
  );

  if (hasMaintenanceError) {
    return "MAINTENANCE_ERROR";
  }

  return "UNKNOWN_ERROR";
}

/**
 * Navega para a página de erro apropriada baseado no tipo de erro
 */
export function routeToErrorPage({
  navigate,
  error,
  context = "general",
  fallbackRoute = "/server-error",
}: ErrorRoutingOptions): void {
  try {
    const errorType = determineErrorType(error);
    const targetRoute = ERROR_TYPE_TO_ROUTE[errorType] || fallbackRoute;

    // Log do erro no sistema de monitoramento
    errorMonitor.logError({
      id: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      message: error?.message || "Erro durante roteamento",
      stack: error?.stack,
      timestamp: Date.now(),
      type: "network",
      severity: "high",
      metadata: {
        context: `error_routing_${context}`,
        errorType,
        targetRoute,
        originalError: error,
      },
      userAgent: navigator.userAgent,
      url: window.location.href,
    });

    // Navegar para a página de erro
    navigate(targetRoute, {
      state: {
        error: {
          type: errorType,
          message: error?.message || "Erro desconhecido",
          status: error?.response?.status,
          timestamp: new Date().toISOString(),
          context,
          originalUrl: window.location.href, // Adicionar URL original
        },
      },
    });
  } catch (routingError) {
    if (process.env.NODE_ENV === "development") {
      console.error("Erro ao rotear para página de erro:", routingError);
    }

    // Fallback para rota padrão
    navigate(fallbackRoute, {
      state: {
        error: {
          type: "UNKNOWN_ERROR",
          message: "Erro interno no sistema de roteamento",
          timestamp: new Date().toISOString(),
          context: "error_routing_failure",
          originalUrl: window.location.href, // Adicionar URL original também no fallback
        },
      },
    });
  }
}

/**
 * Função helper para retry inteligente que volta para a URL original
 */
export function createRetryHandler(errorState?: any): () => void {
  return () => {
    if (errorState?.originalUrl) {
      // Se temos a URL original, navegar para lá
      const originalUrl = errorState.originalUrl;

      // Verificar se é uma URL da mesma origem
      try {
        const url = new URL(originalUrl);
        if (url.origin === window.location.origin) {
          window.location.href = originalUrl;
          return;
        }
      } catch (e) {
        // Se não conseguir parsear a URL, fazer fallback
      }
    }

    // Fallback: reload da página atual ou voltar para home
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = "/";
    }
  };
}

/**
 * Hook personalizado para roteamento de erros
 */
export function useErrorRouting() {
  return {
    routeToErrorPage,
    determineErrorType,
  };
}

/**
 * Middleware para interceptar erros de API e rotear automaticamente
 */
export function createErrorRoutingMiddleware(navigate: NavigateFunction) {
  return (error: any, context: string = "api") => {
    // Apenas rotear para página de erro se for um erro crítico
    const criticalStatuses = [500, 502, 503, 504];
    const shouldRoute =
      !navigator.onLine ||
      criticalStatuses.includes(error?.response?.status) ||
      error?.code === "ERR_NETWORK";

    if (shouldRoute) {
      routeToErrorPage({
        navigate,
        error,
        context,
      });
      return true; // Indica que o erro foi roteado
    }

    return false; // Indica que o erro não foi roteado
  };
}

/**
 * Utilidade para verificar se devemos mostrar página de erro ou apenas notificação
 */
export function shouldShowErrorPage(error: any): boolean {
  const errorType = determineErrorType(error);

  // Em desenvolvimento local, não mostrar página offline
  if (errorType === "NETWORK_ERROR" && isLocalDevelopment()) {
    return false;
  }

  // Sempre mostrar página para estes tipos de erro
  const criticalErrorTypes: ErrorType[] = [
    "NETWORK_ERROR",
    "MAINTENANCE_ERROR",
    "SERVER_ERROR",
  ];

  return criticalErrorTypes.includes(errorType);
}

export default {
  routeToErrorPage,
  determineErrorType,
  useErrorRouting,
  createErrorRoutingMiddleware,
  shouldShowErrorPage,
};
